/**
 * A local draft of the grid's unsaved edits, one per forecast version.
 *
 * Saving is still explicit (nothing reaches the server until Save), but every edit is written to
 * the browser as it happens, so a dropped connection, a failed save or a closed tab no longer
 * costs the work: on the next load of that version the page offers the draft back. A draft is
 * only offered when it differs from what the server holds; once saved (or discarded) it is gone.
 *
 * Browser storage is a convenience, not a record: it can be absent or full, so every read and
 * write is wrapped, and the page works the same without it.
 */

const DRAFT_KEY_PREFIX = "sales_forecasting_draft:"

export interface ForecastDraft {
  version_id: string
  /** When the draft was last written (ISO). */
  saved_at: string
  /** `updated_at` of the version the edits were made on, so a draft from before someone else's save is still offered, flagged. */
  version_updated_at?: string
  /** The grid's `forecastDataSignature` of the roots when written, to skip a draft equal to what is loaded. */
  signature: string
  roots: unknown[]
}

export function draft_storage_key(version_id: string): string {
  return `${DRAFT_KEY_PREFIX}${version_id}`
}

/** Write (or replace) the draft for a version. Returns false when storage refused it (absent, full). */
export function write_forecast_draft(draft: ForecastDraft): boolean {
  try {
    localStorage.setItem(draft_storage_key(draft.version_id), JSON.stringify(draft))
    return true
  } catch {
    return false
  }
}

export function read_forecast_draft(version_id: string): ForecastDraft | null {
  try {
    const raw = localStorage.getItem(draft_storage_key(version_id))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<ForecastDraft>
    if (!parsed || parsed.version_id !== version_id || !Array.isArray(parsed.roots) || typeof parsed.signature !== "string") {
      return null
    }
    return parsed as ForecastDraft
  } catch {
    return null
  }
}

export function clear_forecast_draft(version_id: string): void {
  try {
    localStorage.removeItem(draft_storage_key(version_id))
  } catch {
    // Nothing to clear, or storage is blocked: either way there is no draft to offer.
  }
}

export interface DraftOffer {
  draft: ForecastDraft
  /** The version was saved by someone (or somewhere) after this draft was started. */
  version_changed_since: boolean
}

/**
 * Whether a stored draft should be offered on top of what just loaded: only when it holds
 * something different from the server's copy. A draft equal to the loaded grid is stale and is
 * dropped by the caller.
 */
export function offer_forecast_draft(
  draft: ForecastDraft | null,
  loaded_signature: string,
  version_updated_at: string | undefined
): DraftOffer | null {
  if (!draft) return null
  if (draft.signature === loaded_signature) return null
  return {
    draft,
    version_changed_since: Boolean(draft.version_updated_at && version_updated_at && draft.version_updated_at !== version_updated_at),
  }
}

/** "Draft saved 3:42 PM" — the caption next to the Save button. */
export function draft_saved_caption(saved_at: string | null): string | null {
  if (!saved_at) return null
  const date = new Date(saved_at)
  if (Number.isNaN(date.getTime())) return null
  return `Draft saved ${date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`
}

if (import.meta.vitest) {
  const { describe, expect, it, beforeEach } = import.meta.vitest

  function draft(extra: Partial<ForecastDraft> = {}): ForecastDraft {
    return { version_id: "v1", saved_at: "2026-10-28T10:00:00Z", signature: "[edited]", roots: [{ id: "a" }], ...extra }
  }

  describe("forecast draft storage", () => {
    beforeEach(() => {
      localStorage.clear()
    })

    it("round-trips a draft per version and clears it", () => {
      expect(write_forecast_draft(draft())).toBe(true)
      expect(read_forecast_draft("v1")).toEqual(draft())
      expect(read_forecast_draft("v2")).toBeNull()
      clear_forecast_draft("v1")
      expect(read_forecast_draft("v1")).toBeNull()
    })

    it("ignores a stored value that is not a draft", () => {
      localStorage.setItem(draft_storage_key("v1"), "not json")
      expect(read_forecast_draft("v1")).toBeNull()
      localStorage.setItem(draft_storage_key("v1"), JSON.stringify({ version_id: "v1" }))
      expect(read_forecast_draft("v1")).toBeNull()
    })
  })

  describe("offer_forecast_draft", () => {
    it("offers a draft that differs from the loaded grid", () => {
      expect(offer_forecast_draft(draft(), "[server]", "2026-10-28T09:00:00Z")).toEqual({
        draft: draft(),
        version_changed_since: false,
      })
    })

    it("drops a draft equal to what the server holds", () => {
      expect(offer_forecast_draft(draft({ signature: "[server]" }), "[server]", undefined)).toBeNull()
      expect(offer_forecast_draft(null, "[server]", undefined)).toBeNull()
    })

    it("flags a draft started before the version was saved elsewhere", () => {
      const offer = offer_forecast_draft(draft({ version_updated_at: "2026-10-28T09:00:00Z" }), "[server]", "2026-10-28T09:30:00Z")
      expect(offer?.version_changed_since).toBe(true)
    })
  })

  describe("draft_saved_caption", () => {
    it("names the time and tolerates nothing", () => {
      expect(draft_saved_caption(null)).toBeNull()
      expect(draft_saved_caption("garbage")).toBeNull()
      expect(draft_saved_caption("2026-10-28T10:05:00Z")).toMatch(/^Draft saved \d/)
    })
  })
}
